<template>
	<div id="top">
		<nav class="main-nav">
			<div class="row">
				<div class="columns column12 align-center">
					<div class="logo-div" @click="hideMenu">
						<button class="logo" aria-label="Back to homepage" @click="handleLogoClick" />
					</div>
				</div>

				<nuxt-link :to="localePath('menu')" class="button button-check-menu">
					<span>{{ $t('buttonCheckMenu') }}</span>
				</nuxt-link>

				<div class="mobile-lang-nav">
					<lang-select :items="page.langmenu" />
				</div>

				<div class="nav-bar">
					<div class="nav-content">
						<div class="menu-section">
							<div class="menu-div">
								<div class="nav-button menu-button" :class="{ active: showMenu }" @click="toggleMenu">
									<div class="bars">
										<div class="bar" />
										<div class="bar" />
										<div class="bar" />
									</div>
								</div>
								<ul class="fullscreen-nav" :class="{ active: showMenu }">
									<li class="close">
										<span @click="hideMenu">
											<font-awesome-icon icon="fal fa-times" size="1x" />
										</span>
									</li>
									<li>
										<lang-select :items="page.langmenu" />
									</li>
									<li
										v-for="item in topmenu[locale]"
										:key="item.filename"
										:class="{ hassubmenu: item.submenu.length }"
									>
										<nuxt-link :to="item.filename" @click="hideMenu">
											{{ item.header }}
										</nuxt-link>
										<submenu
											v-if="item.submenu.length"
											:item="item"
											:subitems="item.submenu"
											@hide-nav="hideMenu"
										/>
									</li>
									<li class="lang-nav-mobile">
										<lang-select :items="page.langmenu" />
									</li>
									<li class="socials">
										<social-media class="socials" :socials="socials" />
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</div>
		</nav>
	</div>
</template>

<script setup>
const localeRoute = useLocaleRoute();
const localePath = useLocalePath();

const route = useRoute();
const { page, fetchPage } = usePage();

const { locale } = useI18n();
const { data: topmenu } = await useWebsiteFetch('topmenu');
const showMenu = ref(false);

const { socials } = useDefaults();

await fetchPage();

const handleLogoClick = () => {
	if (route.name?.startsWith('index')) {
		window.scrollTo(0, 0);
	} else {
		const route = localeRoute('index');
		navigateTo(route.fullPath);
	}
};

const toggleMenu = () => {
	showMenu.value = !showMenu.value;
};

const hideMenu = () => {
	showMenu.value = false;
};

const config = useRuntimeConfig();
const reloadWidgets = () => {
	window.hiringAssistant.loadOtherAsync(config.public.soigneId);
};

watch(locale, () => {
	reloadWidgets();
});
</script>

<style lang="scss" scoped>
.hidden {
	display: none !important;
}

.nav-button {
	cursor: pointer;
	text-transform: uppercase;
	letter-spacing: 1px;
	z-index: 99;
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
	align-items: center;
	color: var(--primary-background-color);
	font-size: 28px;
	position: fixed;
	inset: 70px auto auto 30px;

	.bars {
		display: flex;
		flex-flow: column wrap;
		gap: 8px;

		.bar {
			width: 38px;
			height: 3px;
			background: var(--primary-background-color);
			border-radius: 50px;
		}
	}

	span {
		font-weight: 500;
		font-family: var(--body-font-family);
		font-size: calc(var(--body-font-size) + 8px);
	}
}

.close {
	color: var(--primary-background-color);
	position: absolute;
	inset: 50px 15px auto auto;
	cursor: pointer;
	font-size: 22px;
}

.logo {
	width: 240px;
	height: 164px;
	background: url('~/assets/images/logo-arie-goudvisch-full-beige.png') no-repeat center center;
	background-size: 100%;
	outline: none;
	border: none;
	position: relative;
	z-index: 101;
	margin: 75px auto 0;
	display: none;
}

.index .logo,
.menu .logo {
	display: block;
}

.menu-section {
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: flex-start;
	padding: 60px 0 0;
	gap: 30px;
}

.main-nav {
	font-family: var(--heading-font-family);
	position: fixed;
	inset: 0 0 auto;

	ul {
		opacity: 0;
		transition: all 0.4s ease-in-out;
		transform: translate(-100%, 0) matrix(1, 0, 0, 1, 0, 0);
		position: fixed;
		inset: 0 auto 0 0;
		z-index: 100;
		background: var(--secondary-background-color);
		display: flex;
		flex-flow: column wrap;
		justify-content: flex-start;
		align-items: flex-start;
		padding: 180px 30px 0;
		width: 280px;
		max-width: 80%;

		&.active {
			transform: matrix(1, 0, 0, 1, 0, 0);
			opacity: 1;
		}

		li {
			display: inline-block;
			z-index: 1000;
			margin: 11px 0;

			&.socials {
				position: absolute;
				inset: auto auto 30px 30px;
			}

			a {
				text-transform: uppercase;
				text-decoration: none;
				font-weight: 500;
				position: relative;
				padding: 0 3px;
				letter-spacing: 1px;
				font-family: var(--body-font-family);
				font-size: calc(var(--body-font-size) + 5px);
				color: var(--primary-background-color);

				&:hover,
				&.router-link-exact-active {
					color: var(--cta-color);
				}
			}
		}
	}
}

.logo-mobile {
	display: none;
	background-size: 100%;
	width: 160px;
	margin: 0 auto;
	height: 68px;
	position: absolute;
	top: 68px;
	right: 0;
	bottom: auto;
	left: 0;
	z-index: 2;
	border: none;
	outline: none;
}

.logo-desktop {
	background-size: 100%;
	width: 35px;
	margin: 0 auto;
	height: 347px;
	position: fixed;
	top: calc(50% - 175px);
	right: auto;
	bottom: auto;
	left: 20px;
	z-index: 2;
	border: none;
	outline: none;
	cursor: pointer;
}

#top {
	position: relative;
	z-index: 10;
}

.lang-nav-mobile {
	display: none !important;
}

.mobile-lang-nav {
	position: absolute;
	inset: 30px 20px auto auto;
	z-index: 100;
	display: none;

	.lang-nav,
	:deep(.lang-nav) {
		position: relative;
		inset: auto;
	}
}

.button-check-menu {
	position: fixed;
	inset: 66px 22px auto auto;
	font-weight: 400;
	letter-spacing: 1.5px;
	font-style: normal;
	display: none;
	font-size: 22px;
	padding: 9px 41px 8px;
}

.index .button-check-menu {
	display: block;
}

@media (max-width: 1080px) {
	.nav-bar > .lang-nav {
		display: none;
	}

	.lang-nav-mobile {
		display: inline-block !important;
	}

	.menu-section {
		width: 100%;
	}

	.logo {
		z-index: 99;
		width: 172px;
		height: 120px;
	}

	.nav-button {
		inset: 60px auto auto 10px;
		position: absolute;

		&.menu-button {
			position: fixed;
			inset: 72px auto auto 16px;
		}

		&.contact-button {
			inset: 35px 20px auto auto;
		}
	}

	.main-nav {
		position: absolute;

		ul {
			inset: 0;
			width: 100%;
			max-width: 100%;
			text-align: center;
			padding: 140px 30px 0;

			&::before {
				height: 90px;
			}

			&::after {
				height: 106px;
			}

			li:not(.close) {
				width: 100%;
				text-align: left;
				margin: 11px auto;

				a {
					font-size: 29px;
					padding: 0;
				}
			}
		}
	}

	.contact-info {
		inset: 55px 10px 60px auto;
	}

	.logo-mobile {
		display: block;
	}
}

@media (max-width: 900px) and (orientation: landscape) {
	.main-nav {
		ul {
			margin: 30px 0 0;
		}
	}
}

@media (max-width: 700px) {
	.main-nav {
		.row {
			padding-left: 0;
		}

		ul {
			li.socials {
				inset: auto 35px 70px;
				align-items: flex-start;
				text-align: left;
			}
		}
	}

	body.index .mobile-lang-nav {
		display: block;
	}

	.button-check-menu {
		position: absolute;
		inset: 230px 0 auto;
		display: none;
		margin: 0 auto;
		width: auto;
		text-align: center;
		max-width: 240px;
	}

	.index .button-check-menu {
		display: block;
	}

	.nav-button {
		.bars {
			gap: 8px;

			.bar {
				width: 36px;
				height: 2px;
			}
		}
	}
}

@media (max-height: 640px) {
	.main-nav {
		ul {
			margin: 50px 0 0;
		}
	}
}
</style>
